import { useEffect, useRef } from "react";

/**
 * ParticleCanvas — warm sunset "embers / desert dust" particle field.
 * Mouse-reactive swirl, scroll-tied opacity handled by parent via CSS.
 */
export default function ParticleCanvas() {
  const canvasRef = useRef(null);
  const pointer = useRef({ x: 0.5, y: 0.5, active: false });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let width = canvas.clientWidth;
    let height = canvas.clientHeight;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];
    let rafId = null;

    const COLORS = ["#EA580C", "#F59E0B", "#FBBF24", "#9D174D", "#FDBA74"];

    function resize() {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      spawn();
    }

    function spawn() {
      const count = Math.min(180, Math.floor((width * height) / 9000));
      particles = new Array(count).fill(0).map(() => ({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.6 + 0.3,
        vx: (Math.random() - 0.5) * 0.25,
        vy: -Math.random() * 0.35 - 0.05,
        life: Math.random(),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        tw: Math.random() * Math.PI * 2,
      }));
    }

    function step() {
      ctx.clearRect(0, 0, width, height);
      const px = pointer.current.x * width;
      const py = pointer.current.y * height;
      const isActive = pointer.current.active;

      // Update physics for all particles first
      for (const p of particles) {
        if (isActive) {
          const dx = p.x - px;
          const dy = p.y - py;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 25600) { // 160*160
            const dist = Math.sqrt(dist2) || 1;
            const f = (1 - dist / 160) * 0.6;
            p.vx += (dx / dist) * f * 0.6;
            p.vy += (dy / dist) * f * 0.6;
          }
        }
        p.x += p.vx;
        p.y += p.vy;
        p.tw += 0.04;
        p.life += 0.002;
        if (p.y < -10) { p.y = height + 10; p.x = Math.random() * width; }
        if (p.x < -10) p.x = width + 10;
        if (p.x > width + 10) p.x = -10;
        p.vx *= 0.985;
        p.vy = p.vy * 0.99 - 0.005;
      }

      // Draw glow pass (large translucent circle — replaces expensive createRadialGradient)
      for (const p of particles) {
        const alpha = 0.35 + Math.sin(p.tw) * 0.25;
        ctx.globalAlpha = alpha * 0.3;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * 5, 0, Math.PI * 2);
        ctx.fill();
      }

      // Draw core pass (tight solid dot)
      for (const p of particles) {
        const alpha = 0.35 + Math.sin(p.tw) * 0.25;
        ctx.globalAlpha = Math.min(1, alpha + 0.2);
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }

      ctx.globalAlpha = 1;
      rafId = requestAnimationFrame(step);
    }

    function onPointerMove(e) {
      const rect = canvas.getBoundingClientRect();
      pointer.current.x = (e.clientX - rect.left) / rect.width;
      pointer.current.y = (e.clientY - rect.top) / rect.height;
      pointer.current.active = true;
    }
    function onLeave() { pointer.current.active = false; }

    resize();
    step();

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerleave", onLeave);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-canvas"
      className="absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
}
